import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {PROJECT} from '../constants';
import {
    Get,
    Post,
    Put,
    DeleteSingle,
    PostFormData,
    PutFormData
} from '../../utils/httpProvider';
import {PROJECT_API} from '../../utils/apiUrl';

const buildProjectFormData = (model = {}) => {
    const formData = new FormData();

    if (model.name != null) {
        formData.append('Name', model.name);
    }

    if (model.moTa != null) {
        formData.append('MoTa', model.moTa);
    }

    if (model.startDate !== undefined) {
        formData.append('StartDate', model.startDate || '');
    }
    if (model.endDate !== undefined) {
        formData.append('EndDate', model.endDate || '');
    }

    if (model.actualEndDate !== undefined) {
        formData.append('ActualEndDate', model.actualEndDate || '');
    }

    if (Array.isArray(model.quyTrinhIds)) {
        model.quyTrinhIds.forEach((id) => {
            if (id !== null && id !== undefined) {
                formData.append('QuyTrinhIds', id);
            }
        });
    }

    if (Array.isArray(model.teamIds)) {
        model.teamIds.forEach((id) => {
            if (id !== null && id !== undefined) {
                formData.append('TeamIds', id);
            }
        });
    }

    if (Array.isArray(model.files)) {
        model.files.forEach((file) => {
            if (file) {
                formData.append('Files', file);
            }
        });
    }

    if (Array.isArray(model.removeFileIds)) {
        model.removeFileIds.forEach((id) => {
            if (id !== null && id !== undefined) {
                formData.append('RemoveFileIds', id);
            }
        });
    }

    return formData;
};

export const projectGetAllPagingAction = createAsyncThunk(
    PROJECT.GET_ALL_PAGING,
    async (params, {rejectWithValue}) => {
        try {
            const data = await Get(PROJECT_API.GET_ALL_PAGING, null, params);
            if (!data) return rejectWithValue('Không lấy được danh sách dự án');
            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const projectGetAllAction = createAsyncThunk(
    PROJECT.GET_ALL,
    async (_, {rejectWithValue}) => {
        try {
            const data = await Get(PROJECT_API.BASE, null, null);
            if (!data) return rejectWithValue('Không lấy được danh sách dự án');
            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const projectGetByIdAction = createAsyncThunk(
    PROJECT.GET_BY_ID,
    async (id, {rejectWithValue}) => {
        try {
            const data = await Get(PROJECT_API.BY_ID, id, null);
            if (!data) return rejectWithValue('Không tìm thấy dự án');
            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const projectCreateAction = createAsyncThunk(
    PROJECT.CREATE,
    async (model, {rejectWithValue}) => {
        try {
            const formData = buildProjectFormData(model);
            const res = await PostFormData(PROJECT_API.CREATE, null, formData);

            let data = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }

            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const projectUpdateAction = createAsyncThunk(
    PROJECT.UPDATE,
    async ({id, data}, {rejectWithValue}) => {
        try {
            const formData = buildProjectFormData(data);
            const res = await PutFormData(PROJECT_API.UPDATE, id, formData);

            let body = null;
            try {
                body = await res.json();
            } catch (_) {
                body = null;
            }

            // BE có thể trả ProjectDetailDto, nếu không có thì merge tạm
            return body || {id, ...data};
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const projectUpdateStatusAction = createAsyncThunk(
    PROJECT.UPDATE_STATUS,
    async ({id, status}, {rejectWithValue}) => {
        try {
            const url = PROJECT_API.UPDATE_STATUS(id);
            const res = await Put(url, null, {status});

            if (!res || !res.ok) {
                let errBody = null;
                try {
                    errBody = await res.json();
                } catch (_) {
                    errBody = null;
                }
                return rejectWithValue(
                    errBody || 'Cập nhật trạng thái dự án thất bại'
                );
            }

            let body = null;
            try {
                body = await res.json();
            } catch (_) {
                body = null;
            }

            if (body && body.id) {
                return body;
            }

            return {id, trangThai: status};
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const projectDeleteAction = createAsyncThunk(
    PROJECT.DELETE,
    async (id, {rejectWithValue}) => {
        try {
            const res = await DeleteSingle(PROJECT_API.DELETE, id, null);

            if (res && typeof res.status === 'number') {
                if (res.status === 200 || res.status === 204) {
                    return id;
                }

                let errBody = null;
                try {
                    errBody = await res.json();
                } catch (_) {
                    errBody = null;
                }
                return rejectWithValue(errBody || 'Xoá dự án thất bại');
            }

            if (!res) return rejectWithValue('Xoá dự án thất bại');
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

const initialState = {
    list: [],
    item: null,
    loading: false,
    error: null,
    totalRecords: 0,
    pageIndex: 1,
    pageSize: 20
};

export const projectSlice = createSlice({
    name: PROJECT.NAME_SLICE,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(projectGetAllPagingAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(projectGetAllPagingAction.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload || {};
                state.list = payload.data || [];
                state.totalRecords = payload.totalRecords || 0;
                state.pageIndex = payload.pageIndex || 1;
                state.pageSize = payload.pageSize || state.pageSize;
            })
            .addCase(projectGetAllPagingAction.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Lỗi khi tải danh sách dự án';
            })

            .addCase(projectGetAllAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(projectGetAllAction.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload || [];
                state.totalRecords = Array.isArray(action.payload)
                    ? action.payload.length
                    : 0;
            })
            .addCase(projectGetAllAction.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Lỗi khi tải danh sách dự án';
            })

            .addCase(projectGetByIdAction.fulfilled, (state, action) => {
                state.item = action.payload || null;
            })
            .addCase(projectGetByIdAction.rejected, (state, action) => {
                state.item = null;
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Không tải được chi tiết dự án';
            })

            .addCase(projectCreateAction.fulfilled, (state, action) => {
                if (action.payload) {
                    state.list.unshift(action.payload);
                    state.totalRecords += 1;
                }
            })
            .addCase(projectCreateAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Tạo dự án thất bại';
            })

            .addCase(projectUpdateAction.fulfilled, (state, action) => {
                const updated = action.payload;
                if (!updated || !updated.id) return;

                const idx = state.list.findIndex((x) => x.id === updated.id);
                if (idx >= 0) {
                    state.list[idx] = {...state.list[idx], ...updated};
                }

                if (state.item && state.item.id === updated.id) {
                    state.item = {...state.item, ...updated};
                }
            })
            .addCase(projectUpdateAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Cập nhật dự án thất bại';
            })

            .addCase(projectUpdateStatusAction.fulfilled, (state, action) => {
                const updated = action.payload;
                if (!updated || !updated.id) return;

                const idx = state.list.findIndex((x) => x.id === updated.id);
                if (idx >= 0) {
                    state.list[idx] = {...state.list[idx], ...updated};
                }

                if (state.item && state.item.id === updated.id) {
                    state.item = {...state.item, ...updated};
                }
            })
            .addCase(projectUpdateStatusAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Cập nhật trạng thái dự án thất bại';
            })

            .addCase(projectDeleteAction.fulfilled, (state, action) => {
                const id = action.payload;
                state.list = state.list.filter((x) => x.id !== id);
                if (state.totalRecords > 0) state.totalRecords -= 1;
            })
            .addCase(projectDeleteAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Xoá dự án thất bại';
            });
    }
});

export default projectSlice.reducer;
